import React, { useContext, useMemo, useRef, useState } from 'react'
import { useKnowledgeBaseStore } from '../../../../store/knowledgeBaseStore'; 
import dashboardStyles from '../../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from '../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss';
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import globalStyles from "../../../../assets/styles/global.module.scss";
import { tertiaryColor } from '../../../../utils/colors';
import { useVirtualizer } from '@tanstack/react-virtual';
import MemoizedKnowledgeBaseTableRow from './memoizedKnowledgeBaseTableRow';
import { globalMenuItemData, knowledgeBaseFiles } from '../../../../models/axiosModel';
import { UserContext, UserContextPayload } from '../../../../context/userContext'; 
import deleteDoc from "../../../../assets/deleteDoc.svg"; 
import GlobalDeletePopup from '../../../utility/globalDeletePopup/globalDeletePopup';
import agent from '../../../../services/api';
import { useGenericStore } from '../../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import GlobalMenu from '../../../globalMenu';
import IconsColor from '../../../utility/iconsColor';
import GlobalPopUp from '../../../utility/globalPopUp';
import useGenericHook from '../../../../hooks/useGenericHook';
import AssignChatbotPopup from './assignChatbotPopup';
import { getVendorField } from '../../../../utils/whiteLabelling';

export interface sortConfig {
    key: string,
    direction: string,
}

interface KnowledgeBaseTableProps{
    getKnowledgeBaseFilesApiCall: () => Promise<void>
}

const KnowledgeBaseTable: React.FC<KnowledgeBaseTableProps> = (props) => {

    const { getKnowledgeBaseFilesApiCall } = props;

    const knowledgeBaseFiles = useKnowledgeBaseStore((state) => state.knowledgeBaseFiles);
    const { userDetail, setFlagLoading, handleError, vendorData } = useContext(UserContext) as UserContextPayload;

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const { isScreenBelow600 } = useGenericHook();

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const innerHeight = (window.innerHeight - 150);

    const [sortConfig, setSortConfig] = useState<sortConfig>({ key: "", direction: '' });
    const [changeField, setChangeField] = useState<string>(""); 

    const [selectedFile, setSelectedFile] = useState<knowledgeBaseFiles | null>(null); 
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    
    const [flagAssignChatbotPopup, setFlagAssignChatbotPopup] = useState<boolean>(false);

    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);

    const menuItem: globalMenuItemData[] = [ 
        {
            type: "item",
            label: "Assign Chatbot",
            icon: <IconsColor iconName="chatbot" fillColor="#514D53" />,
            onClick: (file: knowledgeBaseFiles) => {
                setSelectedFile(file);
                setFlagAssignChatbotPopup(true);
            }
        },
        {
            type: "item",
            label: "Download",
            icon: <IconsColor iconName="download" height="14" width="14" fillColor={"gray"}  />,
            onClick: (file: knowledgeBaseFiles) => {
                downloadKnowledgeBaseFile(file);
            }
        },
        {
            type: "item",
            label: "Delete",
            icon: <img alt="" src={deleteDoc} />,
            onClick: (file: knowledgeBaseFiles) => {
                setSelectedFile(file);
                setShowDeleteConfirmation(true);
            }
        }
    ]  

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, file: knowledgeBaseFiles) => {
        setSelectedFile(file);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    }; 

    const sortContacts = (key: string, direction: string, files: knowledgeBaseFiles[] | null) => {
        if (files) { 

            const filtered_files = files?.sort((a, b) => {
                let aValue = "", bValue = "";
                if (key === "uploaded_at") {
                    aValue = a.added_timestamp || "";
                    bValue = b.added_timestamp || "";
                } else if(key == "file_name"){
                    aValue = a.file_name || "";
                    bValue = b.file_name || "";
                }
                else if(key == "file_size"){
                    aValue = a.google_storage?.google_storage_file_size || "";
                    bValue = b.google_storage?.google_storage_file_size || "";
                }

                if (aValue === "" && bValue === "") {
                    return 0;
                }
                if (aValue === "") {
                    return direction === 'ascending' ? -1 : 1;
                }
                if (bValue === "") {
                    return direction === 'ascending' ? 1 : -1;
                }
                if (aValue < bValue) {
                    return direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return direction === 'ascending' ? 1 : -1;
                }
                return 0;
            })
            return filtered_files;
        } else return [];
    }
    
    const sortData = (key: string) => {
        setChangeField(key);
        let dir = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            dir = 'descending';
        }
        setSortConfig({ key: key, direction: dir });
    }    

    const getFilteredProducts = useMemo(() => {
        let files = knowledgeBaseFiles ?? [];
        if (sortConfig.key.length > 0 && sortConfig.direction.length > 0) { // Sort the Data.
            files = sortContacts(sortConfig.key, sortConfig.direction, knowledgeBaseFiles) || [];
        } 

        return files;
    }, [knowledgeBaseFiles, sortConfig]);

    const virtualizer = useVirtualizer({
        count: getFilteredProducts?.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 48,
        overscan: 10,
        measureElement:
            typeof window !== 'undefined' && !navigator.userAgent.includes('Firefox')
                ? el => el?.getBoundingClientRect().height
                : undefined,
    });  

    const downloadKnowledgeBaseFile = (file:  knowledgeBaseFiles) => {
        if(file?.google_storage?.google_storage_id){
            fetch(`${getVendorField(vendorData, "backend api", process.env.REACT_APP_BASE_URL)}/api/storage/file?file_id=${file?.google_storage?.google_storage_id}&flag_info=false`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }
                    return response.blob();
                })
                .then(blob => {
                    const fileToDownload = new File([blob], `${file?.file_name}`);
                    const url = URL.createObjectURL(fileToDownload);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileToDownload.name;
                    document.body.appendChild(a);
                    a.click();
                    // Clean up
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                })
                .catch(error => {
                    console.error('There was a problem with the fetch operation:', error);
                });
        }
    }  

    const deleteKnowledgeBaseFileApi = async (selectedFile: knowledgeBaseFiles | null) => {
        if(selectedFile){
            setFlagLoading((prev) => ({ ... prev, flagRoundLoader: true }));
            const res = await agent.deleteKnowledgeBaseFile.delete(selectedFile?.knowledge_base_file_id);
            if(!(res.status)){
                handleError(res);
            } else {
                showSuccess("Knowledge Base File deleted successfully.")
                await getKnowledgeBaseFilesApiCall();
            }
            setShowDeleteConfirmation(false);
            setFlagLoading((prev) => ({ ... prev, flagRoundLoader: false }));
        }
    } 


    return (    
        <>
            <TableContainer className={`${style.contactTableContainer} ${globalStyles.overflowAuto} ${globalStyles.relativePosition}`} ref={tableContainerRef} sx={{ maxHeight: `${innerHeight}px !important` }}>
                <Table stickyHeader>
                    <TableHead sx={{ position: "sticky", top: "0", zIndex: "2" }}> 
                        <TableRow sx={{ display: "flex", width: "100% !important" }}>
                            <TableCell
                                className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth}`}
                                onClick={() => { sortData("file_name") }}
                            >
                                <Grid container className={`${globalStyles.alignItemCenter} ${globalStyles.gapOne} ${globalStyles.fullwidth}`} flexWrap={"nowrap"}>
                                    <Grid item>
                                        Knowledge Base File Name
                                    </Grid>
                                    <Grid item>
                                        <Grid container className={`${globalStyles.flexColumn}`}>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "ascending" && "file_name" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▲</Typography>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "descending" && "file_name" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▼</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </TableCell>
                            <TableCell
                                className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth}`}
                                onClick={() => { sortData("file_size") }}
                            >
                                <Grid container className={`${globalStyles.alignItemCenter} ${globalStyles.gapOne} ${globalStyles.fullwidth}`} flexWrap={"nowrap"}>
                                    <Grid item>
                                        Knowledge Base File Size
                                    </Grid>
                                    <Grid item>
                                        <Grid container className={`${globalStyles.flexColumn}`}>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "ascending" && "file_size" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▲</Typography>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "descending" && "file_size" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▼</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </TableCell> 
                            <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth}`}>
                                Assigned Chatbot
                            </TableCell> 
                            <TableCell
                                className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth}`}
                                onClick={() => { sortData("uploaded_at") }}
                            >
                                <Grid container className={`${globalStyles.alignItemCenter} ${globalStyles.gapOne} ${globalStyles.fullwidth}`} flexWrap={"nowrap"}>
                                    <Grid item>
                                        Uploaded At
                                    </Grid>
                                    <Grid item>
                                        <Grid container className={`${globalStyles.flexColumn}`}>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "ascending" && "uploaded_at" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▲</Typography>
                                            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === "descending" && "uploaded_at" === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>▼</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </TableCell>  
                            <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.checkBoxCellWidth}`} sx={{ background: `${tertiaryColor} !important` }}></TableCell>
                        </TableRow>
                    </TableHead> 

                    <TableBody
                        sx={{
                            display: virtualizer.getVirtualItems().length == 0 ? 'contents' : 'block',
                            height: virtualizer.getVirtualItems().length == 0 ? 'auto' : `${virtualizer.getTotalSize()}px`,
                            position: virtualizer.getVirtualItems().length == 0 ? 'static' : 'relative',
                        }}
                    >
                        {virtualizer.getVirtualItems().length > 0 ? 
                            virtualizer.getVirtualItems().map(virtualRow => {
                                    const data = getFilteredProducts?.[virtualRow.index];
                                    if (!data) return null;

                                    return (
                                        <TableRow
                                            key={data.knowledge_base_file_id}
                                            data-index={virtualRow.index}
                                            ref={node => virtualizer.measureElement(node)}
                                            style={{
                                                position: 'absolute',
                                                transform: `translateY(${virtualRow.start}px)`,
                                                display: 'flex',
                                                width: '100%',
                                            }}
                                        >
                                            <MemoizedKnowledgeBaseTableRow
                                                data={data}
                                                userTimezone={userDetail.timezone_hour}  
                                                handleMenuOpen={handleMenuOpen}
                                            />
                                        </TableRow>
                                    );
                                })
                            : 
                            <TableRow className={`${dashboardStyles.cellRow}`}>
                                <TableCell colSpan={12} className={globalStyles.textCenter}>
                                    No Data Available
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer> 

            <GlobalMenu
                anchorEl={anchorEl}
                handleMenuClose={handleMenuClose}
                menuItemData={menuItem}
                open={Boolean(anchorEl)}
                additionalField={selectedFile}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            />

            {showDeleteConfirmation && 
                <GlobalDeletePopup
                    deleteDescription={`Are you sure you want to delete ${selectedFile?.file_name}`}
                    open={showDeleteConfirmation}
                    onClose={() => { setShowDeleteConfirmation(false); setSelectedFile(null); }}
                    onDelete={() => { deleteKnowledgeBaseFileApi(selectedFile); }}
                />
            } 

            {flagAssignChatbotPopup && 
                <GlobalPopUp
                    open={flagAssignChatbotPopup}
                    onClose={() => setFlagAssignChatbotPopup(false)}
                    handleClose={() => setFlagAssignChatbotPopup(false)}
                    flagShowCloseIcon={true}
                    paperMinWidth={isScreenBelow600 ? "310px" : "600px"}
                    paperMaxwidth={isScreenBelow600 ? "610px" : "650px"}
                    borderRadius='10px'
                    titlePadding='0px'
                >
                    <AssignChatbotPopup 
                        setFlagAssignChatbotPopup={setFlagAssignChatbotPopup}
                        selectedFile={selectedFile}
                        getKnowledgeBaseFilesApiCall={getKnowledgeBaseFilesApiCall}
                    />
                </GlobalPopUp>
            }
        </>
    )
}

export default KnowledgeBaseTable
